import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  ProviderNotConfiguredCard,
  errMessage,
  fmtDate,
  isProviderNotConfigured,
  retryUnlessClientError,
} from './shared.js';
import './infrastructure.css';

interface SpacesKey {
  name: string;
  accessKey: string;
  /** present only on the create response — never retrievable again */
  secretKey: string | null;
  createdAt: string | null;
}

export function SpacesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const keysQuery = useQuery<SpacesKey[]>({
    queryKey: ['do', 'spaces-keys'],
    queryFn: () => apiFetch<SpacesKey[]>('/do/spaces-keys'),
    retry: retryUnlessClientError,
  });

  // ── create ──
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState<SpacesKey | null>(null);

  const createMut = useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch<SpacesKey>('/do/spaces-keys', { method: 'POST', body }),
    onSuccess: async (key) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'spaces-keys'] });
      setCreateOpen(false);
      setName('');
      setCreatedKey(key);
      toast.success(`Spaces key "${key.name}" created`);
    },
    onError: (err) => toast.error(errMessage(err, 'failed to create spaces key')),
  });

  // ── delete ──
  const [deleteTarget, setDeleteTarget] = useState<SpacesKey | null>(null);
  const deleteMut = useMutation({
    mutationFn: (key: SpacesKey) =>
      apiFetch<{ ok: boolean }>(`/do/spaces-keys/${encodeURIComponent(key.accessKey)}`, {
        method: 'DELETE',
        body: { confirmName: key.name },
      }),
    onSuccess: async (_res, key) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'spaces-keys'] });
      toast.success(`Spaces key "${key.name}" deleted`);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMessage(err, 'delete failed')),
  });

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('clipboard copy failed — select and copy manually');
    }
  };

  const columns: Column<SpacesKey>[] = [
    { key: 'name', header: 'Name', render: (k) => <span className="infra-name">{k.name}</span> },
    {
      key: 'accessKey',
      header: 'Access key',
      render: (k) => <span className="mono">{k.accessKey}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (k) => <span className="ui-cell-mute">{fmtDate(k.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (k) => (
        <div className="ui-cell-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => copy('Access key', k.accessKey)}>
            Copy key
          </button>
          <button className="btn btn-ghost-danger btn-sm" onClick={() => setDeleteTarget(k)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  if (isProviderNotConfigured(keysQuery.error)) {
    return (
      <div>
        <div className="page-head">
          <div>
            <h1>Spaces</h1>
            <p className="page-sub">S3-compatible object storage access keys.</p>
          </div>
        </div>
        <ProviderNotConfiguredCard provider="DigitalOcean" message={keysQuery.error.message} />
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Spaces</h1>
          <p className="page-sub">S3-compatible object storage access keys.</p>
        </div>
        <button className="btn btn-amber" onClick={() => setCreateOpen(true)}>
          Create access key
        </button>
      </div>

      <div className="card spaces-note">
        Bucket browsing is not yet available — this page manages S3-protocol access keys for
        DigitalOcean Spaces. Use the keys with any S3-compatible client.
      </div>

      <Table
        columns={columns}
        rows={keysQuery.data ?? []}
        rowKey={(k) => k.accessKey}
        empty={
          keysQuery.isLoading
            ? 'Loading…'
            : keysQuery.isError
              ? errMessage(keysQuery.error, 'failed to load spaces keys')
              : 'No spaces access keys yet. Create one to get started.'
        }
      />

      {/* create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setName('');
        }}
        title="Create spaces access key"
      >
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && !createMut.isPending) createMut.mutate({ name: name.trim() });
          }}
        >
          <div className="field">
            <label htmlFor="spaces-key-name">Name</label>
            <input
              id="spaces-key-name"
              autoFocus
              maxLength={255}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ci-uploads"
            />
          </div>
          <div className="dialog-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setCreateOpen(false);
                setName('');
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-amber"
              disabled={name.trim().length === 0 || createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create key'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* one-time secret reveal */}
      <Dialog
        open={createdKey !== null}
        onClose={() => setCreatedKey(null)}
        title={createdKey ? `Key "${createdKey.name}" created` : 'Key created'}
        width={520}
      >
        {createdKey ? (
          <div className="dialog-form">
            <p className="secret-once-warning">
              Store the secret key now — DigitalOcean shows it exactly once. After you close this
              dialog it can never be retrieved again.
            </p>
            <div className="field">
              <label>Access key</label>
              <div className="secret-copy-row">
                <code>{createdKey.accessKey}</code>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => copy('Access key', createdKey.accessKey)}
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="field">
              <label>Secret key</label>
              <div className="secret-copy-row">
                <code>{createdKey.secretKey ?? '(not returned)'}</code>
                {createdKey.secretKey ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => copy('Secret key', createdKey.secretKey ?? '')}
                  >
                    Copy
                  </button>
                ) : null}
              </div>
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn btn-amber" onClick={() => setCreatedKey(null)}>
                I stored it — close
              </button>
            </div>
          </div>
        ) : null}
      </Dialog>

      {/* delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)}
        title="Delete spaces key"
        targetName={deleteTarget?.name ?? ''}
        description="Anything using this access key loses access to Spaces immediately."
        confirmLabel="Delete key"
        busy={deleteMut.isPending}
      />
    </div>
  );
}
